import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BlogLayout } from '../../components/BlogLayout';
import { PageHeader } from '../../components/PageHeader';
import { api } from '../../lib/api';
import type { Author } from '../../lib/types';

export default function AuthorsPage() {
  const [authors, setAuthors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchAuthors() {
      try {
        const response = await api.get<Author[]>('/api/v1/authors');
        if (response.success && response.data) {
          setAuthors(response.data);
        } else {
          setError(response.error?.message || 'Failed to load authors');
        }
      } catch (err) {
        setError('Failed to load authors');
      } finally {
        setLoading(false);
      }
    }
    fetchAuthors();
  }, []);

  return (
    <BlogLayout>
      <Helmet>
        <title>Authors - QuestFlow</title>
        <meta name="description" content="Meet the QuestFlow voices behind our stories" />
      </Helmet>
      <div className="page-body">
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading authors...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <p>{error}</p>
          </div>
        ) : (
          <>
            <PageHeader title="Voices behind the guides" eyebrow="Authors" />
            <div className="page-header-actions">
              <Link to="/" className="inline-link">
                Back to home
              </Link>
            </div>

            <div className="card-grid">
              {authors.map((author) => (
                <article className="card author-card" key={author.slug}>
                  <div className="card-media circle">
                    <img
                      src={
                        author.avatar_url ||
                        'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=900&q=80'
                      }
                      alt={author.name}
                      style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                    />
                  </div>
                  <div className="card-body">
                    <h3 className="title">
                      <Link to={`/authors/${author.slug}`}>{author.name}</Link>
                    </h3>
                    <p className="excerpt">{author.bio || 'Bio coming soon.'}</p>
                    <div className="meta-row">
                      <span className="byline">{author.style || 'Distinct AI voice'}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}
      </div>
    </BlogLayout>
  );
}
